// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {ChangeEvent, useCallback, useMemo, useState} from 'react';

import {useAccountTeamsList} from '../../../../pages/HomePage/HomePageSidebar/useAccountTeamsList';
import {useIsRestrictedAccessOptionEnabled} from '../../../../pages/HomePage/HomePageSidebar/useIsRestrictedAccessOptionEnabled';
import {useViewer} from '../../../../state/viewer/hooks';
import {AccessOption} from '../../../../util/permissions';
import {useRampFlagAccountSelector} from '../../../../util/rampFeatureFlags';
import {FixedSizeTextArea} from '../../../Form/FixedSizeTextArea';
import {ArtifactTypeInfoBanner} from '../../common/ArtifactTypeInfoBanner';
import {ArtifactTypeMultiInput} from '../../common/ArtifactTypeMultiInput';
import {RegistryAccessOptionType} from '../../common/permissionUtils';
import {RegistryAccessOptions} from '../../common/RegistryAccessOptions';
import {RegistryArtifactTypesOptions} from '../../common/RegistryArtifactTypesOptions';
import {RestrictedRegistryWarningBanner} from '../../common/RestrictedRegistryWarningBanner';
import {REGISTRY_PROJECT_PREFIX} from '../../common/utils';
import {FAQs} from './FAQs';
import {useUpsertRegistryProjectWithTypes} from './hooks';

export const DEFAULT_ACCESS_OPTIONS: RegistryAccessOptionType[] = [
  AccessOption.Private,
  AccessOption.Restricted,
];

export type RegistryProject = {
  projectName: string;
  registryName: string;
  description: string;
  access: RegistryAccessOptionType;
  allowAllArtifactTypesInRegistry?: boolean;
};

const EMPTY_REGISTRY_PROJECT: RegistryProject = {
  registryName: '',
  projectName: '',
  description: '',
  access: AccessOption.Private,
};

type CreateRegistryDrawerInnerProps = {
  scrolled: {scrolled: boolean; scrollHeight: number};
  closeDrawer: () => void;
  organizationId?: string;
  organizationName: string;
  entityName: string;
  existingRegistryNames: Set<string>;
};
export const CreateRegistryDrawerInner = ({
  scrolled,
  closeDrawer,
  organizationId,
  organizationName,
  entityName,
  existingRegistryNames,
}: CreateRegistryDrawerInnerProps) => {
  const viewer = useViewer();
  const enableAccountSelector = useRampFlagAccountSelector();
  const {accountTeams} = useAccountTeamsList();
  const teams = accountTeams?.edges?.map(team => team?.node) ?? [];
  const exampleTeamEntityInSelectedAccount =
    enableAccountSelector && teams.length > 0
      ? teams[0]?.name
      : viewer?.defaultEntity?.name;
  const [registryProject, setRegistryProject] = useState<RegistryProject>(
    EMPTY_REGISTRY_PROJECT
  );
  const [inputArtifactTypes, setInputArtifactTypes] = useState<string[]>([]);
  const {upsertRegistryProjectWithTypes, isLoading: isUpsertLoading} =
    useUpsertRegistryProjectWithTypes(organizationName);

  const getIsRegistryNameTaken = useCallback(() => {
    const trimmedRegistryName = registryProject.registryName.trim();
    return existingRegistryNames.has(trimmedRegistryName.toLowerCase());
  }, [registryProject.registryName, existingRegistryNames]);
  const handleProjectNameInputChange = useCallback(
    (value: string) => {
      const invalidUrlChars = '/\\#?%:'.split('');
      if (invalidUrlChars.some(c => value.includes(c))) {
        return;
      }
      setRegistryProject({
        ...registryProject,
        registryName: value,
        projectName: `${REGISTRY_PROJECT_PREFIX}${value}`,
      });
    },
    [registryProject]
  );

  const getIsSubmitDisabled = useCallback(() => {
    if (
      registryProject.allowAllArtifactTypesInRegistry === false &&
      inputArtifactTypes.length === 0
    ) {
      return true;
    }
    return (
      getIsRegistryNameTaken() ||
      registryProject.access == null ||
      registryProject.registryName === '' ||
      registryProject.projectName === '' ||
      registryProject.allowAllArtifactTypesInRegistry == null ||
      isUpsertLoading
    );
  }, [
    getIsRegistryNameTaken,
    registryProject.access,
    registryProject.projectName,
    registryProject.registryName,
    registryProject.allowAllArtifactTypesInRegistry,
    isUpsertLoading,
    inputArtifactTypes,
  ]);

  const {isRestrictedOptionEnabled} =
    useIsRestrictedAccessOptionEnabled(organizationId);
  const accessOptions = useMemo(() => {
    const registryAccessOptions: RegistryAccessOptionType[] = [];
    registryAccessOptions.push(...DEFAULT_ACCESS_OPTIONS);
    return registryAccessOptions;
  }, []);

  const onClose = () => {
    setRegistryProject(EMPTY_REGISTRY_PROJECT);
    closeDrawer();
  };
  const drawerPaddingTop = `${Math.max(80 - scrolled.scrollHeight, 20)}px`;
  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <div
        className={`h-full w-full px-24 pb-20`}
        style={{paddingTop: drawerPaddingTop}}>
        <div className="flex h-full w-full flex-col">
          <div className="mb-20 flex items-center">
            <div className="text-xl font-semibold leading-8">New registry</div>
            <div className="flex-1 text-right">
              <Button
                onClick={onClose}
                icon="close"
                size="large"
                variant="ghost"
              />
            </div>
          </div>
          <div className="-mx-20 h-1 bg-moon-250"></div>
          <div className="w-full flex-1 overflow-auto p-4">
            <div className="mb-8 flex w-full items-center">
              <div className="text-base font-semibold leading-8">Name</div>
            </div>
            <div className="text-base font-normal">
              <TextField
                placeholder="Name this registry Ex. Forecast"
                value={registryProject.registryName ?? ''}
                onChange={handleProjectNameInputChange}
                autoFocus={true}
                errorState={getIsRegistryNameTaken() && !isUpsertLoading}
              />
              {getIsRegistryNameTaken() && !isUpsertLoading && (
                <div className="py-8 text-sm text-red-550">
                  This name is taken
                </div>
              )}
            </div>
            <div className="mb-8 mt-16 flex w-full items-center">
              <div className="text-base font-semibold leading-8">
                Description{' '}
                <span className="text-base font-normal italic leading-8 text-moon-500">
                  (optional)
                </span>
              </div>
            </div>
            <div className="text-base font-normal">
              <FixedSizeTextArea
                placeholder="Describe this registry's use case"
                value={registryProject.description ?? ''}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setRegistryProject({
                    ...registryProject,
                    description: event.target.value,
                  });
                }}
              />
            </div>
            <div className="mb-8 mt-16 flex w-full items-center">
              <div className="text-base font-semibold leading-8">
                Registry visibility
              </div>
            </div>
            <div className="text-base font-normal">
              <RegistryAccessOptions
                isRestrictedProjectEnabled={isRestrictedOptionEnabled ?? false}
                selectedRegistryAccess={registryProject.access}
                setSelectedRegistryAccess={value =>
                  setRegistryProject({...registryProject, access: value})
                }
                registryAccessOptions={accessOptions}
              />
            </div>
            {registryProject.access === AccessOption.Restricted && (
              <RestrictedRegistryWarningBanner />
            )}
            <div className="mb-8 mt-16 flex w-full items-center">
              <div className="text-base font-semibold leading-8">
                Accepted artifact types
              </div>
              <Tooltip
                trigger={<Icon name="info" style={{marginLeft: '8px'}} />}
                content="Define the accepted artifact types for the registry."
              />
            </div>
            <div className="text-base font-normal">
              <RegistryArtifactTypesOptions
                selectedArtifactTypeConfig={
                  registryProject.allowAllArtifactTypesInRegistry
                }
                setSelectedArtifactTypeConfig={(allowAllTypes: boolean) => {
                  setRegistryProject({
                    ...registryProject,
                    allowAllArtifactTypesInRegistry: allowAllTypes,
                  });
                }}
              />
              {registryProject.allowAllArtifactTypesInRegistry != null &&
                !registryProject.allowAllArtifactTypesInRegistry && (
                  <div className="mt-20">
                    <ArtifactTypeMultiInput
                      size="medium"
                      inputArtifactTypes={inputArtifactTypes}
                      setInputArtifactTypes={setInputArtifactTypes}
                    />
                  </div>
                )}
            </div>
            {inputArtifactTypes.length > 0 && <ArtifactTypeInfoBanner />}
            <div className="mt-16">
              <FAQs entityName={exampleTeamEntityInSelectedAccount} />
            </div>
          </div>
          <div className="-mx-20 h-1 bg-moon-250"></div>
          <div className="flex pt-20">
            <Button
              className="w-full"
              variant="primary"
              size="large"
              disabled={getIsSubmitDisabled()}
              onClick={async () => {
                await upsertRegistryProjectWithTypes({
                  upsertAction: 'Create',
                  description: registryProject.description,
                  registryProjectName: registryProject.projectName,
                  entityName,
                  access: registryProject.access,
                  inputArtifactTypes,
                  allowAllArtifactTypesInRegistry:
                    registryProject.allowAllArtifactTypesInRegistry,
                });
                onClose();
              }}>
              Create registry
            </Button>
          </div>
        </div>
      </div>
    </Tailwind>
  );
};
